import { lastValueFrom } from 'rxjs';

import { getI18NextConfigForLocale } from '@/utils/locales';
import { sanitizeObject, isFeatureEnabled } from '@/utils/millesimaUtils';
import { prefetchLayout } from '@/utils/prefetchLayout';
import {
  NS
} from '@/constants';
import { getBlogMenu } from '@/redux/epics/blog.epics';
import { ARTICLE_CARD_TYPE, BLOG_PAGE_TYPE } from '@/constants/blog';
import { BLOG_SERVER_URL } from '@/constants/urls';
import { FEATURES } from '@/constants/features';
import { FOOTER_TYPE } from '@/constants/footer';
import { HEADER_TYPE } from '@/constants/header';
import { PAGES } from '@/constants/pages';

const NAMESPACES = [
  NS.COMMON,
  NS.BLOG
];

export const getComponentType = (data) => {
  return data?.articleCategForFront?.type || data?.tagForFront?.type || data?.articleForFront?.type || data?.type;
};

export const getFormatedBlogData = (data, componentType) => {
  switch (componentType) {
    case BLOG_PAGE_TYPE.BLOG_HOME:
    case BLOG_PAGE_TYPE.BLOG_CATEG:
      return data.articleCategForFront;
    case BLOG_PAGE_TYPE.BLOG_TAG:
      return data.tagForFront;
    case BLOG_PAGE_TYPE.BLOG_ARCHIVE:
      return data;
    case BLOG_PAGE_TYPE.BLOG_ARTICLE:
      return data.articleForFront;
  }

  return data;
};

export const getBlogServerSideProps = async (ctx, store, serverSideTranslations, sendFacebookEvent, isArchivePage = false) => {
  const { seoService } = store.services;
  const { req, query, resolvedUrl } = ctx;
  const params = new URLSearchParams(resolvedUrl.split('?')[1]);
  const page = +params.get('page') || 1;
  const tagFilter = params.get('filter') || '';
  const slug = query?.blogSlug?.[0] || query?.archiveSlug || query?.tagSlug || query?.tagSlugWithRefacto;
  const layoutOptions = await prefetchLayout(ctx, store);
  const storeState = store.getState();
  const { locale, storeId, features, url } = storeState?.root.storeConf || ctx;
  const { user: { content: userData } } = storeState;
  const translate = await serverSideTranslations(locale, NAMESPACES, getI18NextConfigForLocale(locale));
  let blogData;
  const currentPage = `/${ctx.resolvedUrl.split('/')[1].split('?')[0]}`;

  if (!isFeatureEnabled({ features }, FEATURES.MODULE_BLOG) && currentPage.includes(PAGES.BLOG)) {
    return {
      redirect: {
        destination: url
      }
    };
  }

  try {
    // We send "index.html" if there isn't slug to get homepage data
    blogData = await lastValueFrom(
      seoService.getBlogData({
        storeId, url: slug || 'index.html', isArchivePage, page, tagFilter
      })
    );
    await store.dispatch(getBlogMenu());
  } catch (error) {
    console.error('getBlogServerSideProps error', error);
  }

  sendFacebookEvent({ req, user: userData, eventName: 'PageView', storeId });

  const componentType = getComponentType(blogData);

  if (!blogData) {
    return {
      notFound: true
    };
  } else if (blogData.redirect) {
    return {
      redirect: {
        destination: blogData.redirect,
        permanent: false
      }
    };
  } else {
    const formatedBlogData = getFormatedBlogData(blogData, componentType);

    return {
      props: sanitizeObject({
        storeConf: storeState.root.storeConf,
        slug,
        data: formatedBlogData,
        componentType,
        ...translate,
        layoutOptions: {
          ...layoutOptions,
          withPromotionHeader: false,
          headerType: HEADER_TYPE.BLOG,
          withNavBar: false,
          footerType: FOOTER_TYPE.BLOG,
          withWineBoxModal: false
        },
        initialReduxState: store.getState()
      })
    };
  }
};

export const formatBlogMenuData = (blogMenu) => {
  return blogMenu?.blogMenuLinks.map((link) => {
    return { url: link.url === PAGES.BLOG_HOME_URL ? PAGES.BLOG : link.url, title: link.title };
  });
};

export const formatBreadcrumbData = (breadcrumbSeo) => {
  return breadcrumbSeo?.filter((breadcrumbItem) => {
    return breadcrumbItem?.position !== 0;
  }).map((link) => ({
    ...link,
    url: PAGES.BLOG + '/' + link.url
  }));
};

export const firstTitleLetterToUpper = (archiveDate, locale) => {
  const title = convertArchiveDate(archiveDate, locale);
  return `${title.charAt(0).toUpperCase()}${title.slice(1)}`;
};

export const getPaginationMetaTag = (results, currentPageUrl) => {
  let urlPrev = '';
  let urlNext = '';
  const page = results.number + 1;

  if (page > 1) {
    const pageParam = page > 2 ? `?page=${page - 1}` : '';
    urlPrev = `${currentPageUrl}${pageParam}`;
  }
  if (results.totalElements > page * results.pageable.pageSize) {
    urlNext = `${currentPageUrl}?page=${page + 1}`;
  }

  return {
    urlPrev,
    urlNext
  };
};

export const getTags = (articleContent) => {
  const tags = articleContent.tags;

  return tags.map((tag) => {
    const content = tag.content[0];
    return {
      id: tag.id,
      title: content.title,
      urlKeyword: `${PAGES.BLOG}/${content.urlKeyword}`,
    };
  });
};

export const getCategPageTags = (articleContent, t) => {
  const tags = getTags(articleContent);
  const homepageTag = [
    { id: '', title: t('allArticles', { ns: NS.BLOG }), urlKeyword: PAGES.BLOG }
  ];

  return homepageTag.concat(tags);
};

export const getArticleCardTypeAccordingToIndex = (index) => {
  return index % 2 === 0 ? ARTICLE_CARD_TYPE.SMALL : ARTICLE_CARD_TYPE.MEDIUM;
};

export const convertArchiveDate = (archiveDate, locale = 'fr-FR') => {
  if (!archiveDate?.match(/(0[1-9]|10|11|12)(-\d{4})/)) return archiveDate;
  const [monthNumber, yearNumber] = archiveDate.split('-');
  const date = new Date(yearNumber, monthNumber - 1);

  return date.toLocaleString(locale, { month: 'long', year: 'numeric' });
};

export const sliceListIntoGroups = (sizeListPerGroup, listToSlice) => {
  if (!Array.isArray(sizeListPerGroup) || sizeListPerGroup.length === 0 || sizeListPerGroup.includes(0)) {
    return [listToSlice];
  }

  let result = [];

  const nbGroups = sizeListPerGroup.length;

  const getSizeListPerGroup = (j) => sizeListPerGroup[j % nbGroups];

  for (let i = 0, j = 0; i < listToSlice.length; i += getSizeListPerGroup(j), j += 1) {
    const chunk = listToSlice.slice(i, i + getSizeListPerGroup(j));
    result = result.concat([chunk]);
  }

  return result;
};

export const getBlogImageSrc = (imgName, isMobile, articleCardType) => {
  const folderSmall = 'h412px/';
  const folderMedium = 'h630px/';
  const folderLarge = 'h1280px/';

  if (isMobile || articleCardType === ARTICLE_CARD_TYPE.SMALL) {
    return BLOG_SERVER_URL + folderSmall + imgName;
  }

  if (articleCardType === ARTICLE_CARD_TYPE.MEDIUM) {
    return BLOG_SERVER_URL + folderMedium + imgName;
  }

  return BLOG_SERVER_URL + folderLarge + imgName;
};

export const getParamsForUrl = ({ currentPageUrl, page, filter }) => {
  const queryParams = [
    page && page !== 1 && `page=${page}`,
    filter && `filter=${filter}`
  ].filter(Boolean).join('&');

  return currentPageUrl + (queryParams ? `?${queryParams}` : '');
};

export const updateUrlWithParams = ({ router, currentPageUrl, page, filter }) => {
  // Shallow routing just changes the URL, but the page don't reload itself
  router.push(router.pathname, getParamsForUrl({ currentPageUrl, page, filter }), { shallow: true });
};
